import type { DocSummary } from "./types.js";

/** Compose the case-folded haystack a doc is matched against. Deliberately
 * wide so search finds a file no matter HOW the operator remembers it — by
 * title, by a word in the summary, by its category folder, by its release
 * version, by a tag / solution-area pillar, by doc type, or by slug. This is
 * what makes "find any file in any release and category" hold. */
export function docHaystack(d: DocSummary): string {
  return [
    d.title,
    d.summary ?? "",
    d.category?.name ?? "",
    d.default_release ? `v${d.default_release.version} ${d.default_release.version}` : "",
    d.doc_type,
    ...d.tags.map((t) => t.name),
    ...d.solution_areas.map((s) => s.name),
    d.slug,
  ]
    .join("  ")
    .toLowerCase();
}

/** Filter + rank the catalogue for a free-text query. Every whitespace-split
 * token must appear SOMEWHERE in the doc's haystack (AND semantics), so adding
 * words narrows results. Ranking favours title hits so the most on-topic file
 * floats up:
 *   exact-title-substring > title-prefix > #title-tokens-hit > recency.
 * Pure (no React / no I/O) so it's unit-testable and reusable. */
export function searchDocs(docs: readonly DocSummary[], query: string): DocSummary[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const tokens = q.split(/\s+/).filter(Boolean);

  const scored: Array<{ doc: DocSummary; score: number }> = [];
  for (const doc of docs) {
    const hay = docHaystack(doc);
    if (!tokens.every((t) => hay.includes(t))) continue;

    const title = doc.title.toLowerCase();
    let score = 0;
    if (title.includes(q)) score += 1000;
    if (title.startsWith(q)) score += 500;
    score += tokens.filter((t) => title.includes(t)).length * 50;
    // Newer docs win ties so a fresh upload isn't buried under old versions.
    const ts = Date.parse(doc.updated_at || doc.created_at || "") || 0;
    scored.push({ doc, score: score * 1e13 + ts });
  }

  scored.sort((a, b) => b.score - a.score || a.doc.title.localeCompare(b.doc.title));
  return scored.map((s) => s.doc);
}
